import { UsageHelperBanner } from "@/components/shared/banners/usage-helper-banner";
import { InvoiceOverviewTable } from "@/features/invoice-overview/invoiceOverviewTable";
import { useStandardInitialQuery } from "@/features/todos/initial-filters";
import { useServerSideQuery } from "@/hooks/useQueryFilters";
import { useTablePagination } from "@/hooks/useTablePagination";
import type { InvoiceTypeString } from "@/lib/constants/invoice-type";
import { InvoicesProvider } from "@/providers/invoices";
import { useGetAllInvoices } from "@/queries/invoices/get-all";
import type { InvoiceFilterFields } from "@/types/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export default function TodosStandard({ type }: { type: InvoiceTypeString }) {
  const storageKey = useMemo(() => {
    return `todos-standard-${type}`;
  }, [type]);

  const { t } = useTranslation();
  const { query } = useStandardInitialQuery({ type });
  const { page, setPage, limit, setLimit } = useTablePagination();

  const {
    globalSearch,
    setGlobalSearch,
    serverSideFilters,
    filters,
    setFilters,
    sorting,
    onSortingChange,
    sort,
    order,
    initialServerSideFilters,
    resetFilters,
  } = useServerSideQuery<InvoiceFilterFields>({
    storageKey,
    defaultFilters: query,
    defaultSorting: {
      sort: "supplierName",
      order: "asc",
    },
  });

  const actionsConfig = useMemo(
    () => ({
      exportButton: {
        isVisible: false,
      },
    }),
    [],
  );

  const filtersConfig = useMemo(
    () => ({
      showStatusFilter: false,
      showExportsFilter: false,
      showUploadButton: false,
    }),
    [],
  );

  const columnVisibility = useMemo(
    () => ({
      type: false,
      invoiceNo: true,
      invoiceDate: true,
      startDate: true,
      expenseAccount: true,
      datevBu: true,
      tags: false,
      createdAt: true,
      status: false,
      reviewType: false,
      ocrReviewed: false,
      isBooking: false,
      isDuplicate: false,
    }),
    [],
  );

  const initialSortingOrder = useMemo(() => {
    return [
      {
        id: "supplierName",
        desc: false,
      },
    ];
  }, []);

  const {
    data: invoices,
    error,
    loading,
    pagination,
  } = useGetAllInvoices({
    query: serverSideFilters,
    global: globalSearch,
    sort,
    order,
    page,
    limit,
  });

  return (
    <div className="flex flex-col h-full gap-4">
      <UsageHelperBanner label={t("todos.standard_explainer")} />
      <div className="flex-1 min-h-0">
        <InvoicesProvider invoices={invoices} loading={loading}>
          <InvoiceOverviewTable
            tableName="standard"
            showConfidences
            showStartReviewButton={true}
            actionsConfig={actionsConfig}
            filtersConfig={filtersConfig}
            showUploadProgress={false}
            columnVisibility={columnVisibility}
            initialSortingOrder={initialSortingOrder}
            totalItems={pagination.totalItems}
            currentPage={page}
            totalPages={pagination.totalPages}
            pageSize={limit}
            onPageChange={setPage}
            onPageSizeChange={setLimit}
            filters={filters}
            setFilters={setFilters}
            globalSearch={globalSearch}
            setGlobalSearch={setGlobalSearch}
            sorting={sorting}
            onSortingChange={onSortingChange}
            error={error}
            invoiceType={type}
            initialServerSideFilters={initialServerSideFilters}
            globalServerSideSearch={globalSearch}
            serverSideFilters={serverSideFilters}
            resetFilters={resetFilters}
          />
        </InvoicesProvider>
      </div>
    </div>
  );
}
